import { noop } from "lodash-es";
import { observer } from "mobx-react-lite";
import { AccountProfileImage } from "@/components/layout/components/account-profile/AccountProfileImage";
import { css } from "@/domains/emotion";
import {
  SyncModelPermissionEntry,
  SyncModelPermissionEntrySpaceAccount,
  SyncModelPermissionEntryStatus,
} from "@/domains/sync-scopes/types";
import { useAppStore } from "@/store";
import { ShareSheetPermissionRowLayout } from "@/components/modal/share-sheet/share-sheet-permission-row/share-sheet-permission-row-layout";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { ProfileKind, ProfileSize } from "@/components/layout/components/account-profile";
import { MDSText, MDSTextOverflow, MDSTextVariant } from "@/mds";
import styled from "@emotion/styled";

interface ShareSheetSpaceAccountPermissionRowProps {
  permission: SyncModelPermissionEntrySpaceAccount & SyncModelPermissionEntryStatus;
  isUnsharing: boolean;
  canUpdateAccess: boolean;
  canRevokeAccess: boolean;
  handleRevokeAccess: (modelAccessData: SyncModelPermissionEntry) => void;
  handleUpdateAccess: (modelAccessData: SyncModelPermissionEntry) => void;
  handleCancelOperation: (syncOperationId: string) => void;
  shareSheetModalStore: ShareSheetModalStore;
}

export const ShareSheetSpaceAccountPermissionRow =
  observer<ShareSheetSpaceAccountPermissionRowProps>(
    ({
      permission,
      isUnsharing,
      canUpdateAccess,
      canRevokeAccess,
      handleRevokeAccess,
      handleUpdateAccess,
      handleCancelOperation,
      shareSheetModalStore,
    }) => {
      const { store } = useAppStore();
      const spaceAccountId = permission.space_account_id;
      const spaceAccountObservable = store.spaceAccounts.getSpaceAccount(spaceAccountId);
      const contact = store.contacts.getBySpaceAccountId(spaceAccountId);

      if (spaceAccountObservable) {
        const { profileDisplayName, profileEmailAddress } = spaceAccountObservable;
        return (
          <ShareSheetPermissionRowLayout
            permission={permission}
            isUnsharing={isUnsharing}
            canUpdateAccess={canUpdateAccess}
            canRevokeAccess={canRevokeAccess}
            handleRevokeAccess={() =>
              handleRevokeAccess({
                scope_kind: "SPACE_ACCOUNT_SCOPE",
                space_account_id: spaceAccountId,
                role_kind: "REVOKED",
              })
            }
            handleUpdateAccessToReadOnly={noop}
            handleUpdateAccessToReadWrite={noop}
            handleCancelOperation={handleCancelOperation}
          >
            <div className={container}>
              <div className={profilePhoto}>
                <AccountProfileImage
                  size={ProfileSize.Large}
                  profile={{
                    kind: ProfileKind.Me,
                  }}
                />
              </div>
              <MDSText variant={MDSTextVariant.TextS} overflow={MDSTextOverflow.EllipsisWrap}>
                {`${profileDisplayName} · `}
                {profileEmailAddress && (
                  <StyledSecondaryText>{profileEmailAddress}</StyledSecondaryText>
                )}
              </MDSText>
            </div>
          </ShareSheetPermissionRowLayout>
        );
      }

      if (contact) {
        return (
          <ShareSheetPermissionRowLayout
            permission={permission}
            isUnsharing={isUnsharing}
            canUpdateAccess={canUpdateAccess}
            canRevokeAccess={canRevokeAccess}
            handleRevokeAccess={() => {
              if (
                shareSheetModalStore.collection &&
                shareSheetModalStore.collection.sharedWithSpaceAccountsCount === 1
              ) {
                shareSheetModalStore.makeCollectionPrivateModal.open({
                  collectionId: shareSheetModalStore.collection.id,
                  targetSpaceAccountId: spaceAccountId,
                });
              } else {
                handleRevokeAccess({
                  scope_kind: "SPACE_ACCOUNT_SCOPE",
                  space_account_id: spaceAccountId,
                  role_kind: "REVOKED",
                });
              }
            }}
            handleUpdateAccessToReadOnly={() =>
              handleUpdateAccess({
                scope_kind: "SPACE_ACCOUNT_SCOPE",
                space_account_id: spaceAccountId,
                role_kind: "VIEWER",
              })
            }
            handleUpdateAccessToReadWrite={() =>
              handleUpdateAccess({
                scope_kind: "SPACE_ACCOUNT_SCOPE",
                space_account_id: spaceAccountId,
                role_kind: "EDITOR",
              })
            }
            handleCancelOperation={handleCancelOperation}
          >
            <div className={container}>
              <div className={profilePhoto}>
                <AccountProfileImage
                  size={ProfileSize.Large}
                  profile={{
                    kind: ProfileKind.Contact,
                    data: contact,
                  }}
                />
              </div>
              <MDSText variant={MDSTextVariant.TextS} overflow={MDSTextOverflow.EllipsisWrap}>
                {`${contact.profileDisplayName} · `}
                {contact.profileEmailAddress && (
                  <StyledSecondaryText>{contact.profileEmailAddress}</StyledSecondaryText>
                )}
              </MDSText>
            </div>
          </ShareSheetPermissionRowLayout>
        );
      }

      return null;
    }
  );

const container = css({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});

const profilePhoto = css({
  marginRight: "8px",
});

const StyledSecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;
